import React, { useEffect, useRef, useState } from 'react'
import { TranscriptMessage } from '../../../types/transcript'
import { analytics } from '../../../utils/analytics'

interface IdleModalProps {
  contextParam: string | null
  isSocketConnected: boolean
  isWebRtcConnected: boolean
  transcript: TranscriptMessage | null
}

const initialTime = 60
const idleTime = 3 * 60000
const refreshTime = 60000

export const IdleModal: React.FC<IdleModalProps> = ({
  contextParam,
  isSocketConnected,
  isWebRtcConnected,
  transcript,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [timeLeft, setTimeLeft] = useState(initialTime)

  const idleTimeout = useRef<NodeJS.Timeout | null>(null)
  const idleCountdownTimeout = useRef<NodeJS.Timeout | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const handlePageReload = () => {
    window.location.replace(
      `${location.pathname}${contextParam ? `?context=${contextParam}` : ''}`,
    )
  }

  const handleUpdateIdleTimeout = () => {
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current)
      idleTimeout.current = null
    }
    handleKeepSession(false)
    idleTimeout.current = setTimeout(() => {
      handleUpdateIdleCountdownTimeout(true)
    }, idleTime)
  }

  const handleUpdateIdleCountdownTimeout = (show: boolean) => {
    if (idleCountdownTimeout.current) {
      clearTimeout(idleCountdownTimeout.current)
      idleCountdownTimeout.current = null
    }
    setShowModal(show)
    if (show) {
      analytics.logEvent(
        contextParam
          ? 'watch_session_inactivity_popup_show'
          : 'regular_session_inactivity_popup_show',
      )
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1)
      }, 1000)
    }

    idleCountdownTimeout.current = setTimeout(() => {
      handlePageReload()
    }, refreshTime)
  }

  const handleKeepSession = (refreshTimer = true) => {
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current)
      idleTimeout.current = null
    }

    if (refreshTimer) {
      handleUpdateIdleTimeout()
    }

    if (!refreshTimer && idleCountdownTimeout.current) {
      clearTimeout(idleCountdownTimeout.current)
      idleCountdownTimeout.current = null
    }

    setShowModal(false)
    setTimeLeft(initialTime)
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const handleClick = () => {
    analytics.logEvent(
      contextParam
        ? 'watch_session_inactivity_popup_hide'
        : 'regular_session_inactivity_popup_hide',
    )
    handleKeepSession()
  }

  const clearTimeouts = () => {
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current)
      idleTimeout.current = null
    }
    if (idleCountdownTimeout.current) {
      clearTimeout(idleCountdownTimeout.current)
      idleCountdownTimeout.current = null
    }
    setShowModal(false)
    setTimeLeft(initialTime)
  }

  useEffect(() => {
    if (isWebRtcConnected) {
      handleUpdateIdleCountdownTimeout(false)
    }
    return clearTimeouts
  }, [isWebRtcConnected])

  useEffect(() => {
    if (isSocketConnected) {
      handleKeepSession()
    }
  }, [isSocketConnected])

  useEffect(() => {
    if (transcript && isSocketConnected) {
      handleUpdateIdleTimeout()
    }
  }, [transcript, isSocketConnected])

  if (!showModal) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className=" tc-mv-blur rounded-regular p-6 max-w-sm w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white text-xl font-semibold">
            Are You Still There?
          </h2>
          <button
            onClick={handleClick}
            className="text-gray-500 hover:text-white"
          >
            <i className="fa-solid fa-xmark text-xl text-white" />
          </button>
        </div>
        <p className="text-white mb-6">
          Your session will expire in {timeLeft} seconds due to inactivity.
          Please click the “I’m still here” button if you want to keep going.
        </p>
        <div className="flex justify-end gap-4">
          <button
            data-testid="still-here-button"
            onClick={handleClick}
            className="btn bg-green-100 hover:bg-green-200 transition-bg"
          >
            I’m still here
          </button>
        </div>
      </div>
    </div>
  )
}
